// Qualitätssicherung — Aufgaben und Dokumente.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type QsStatus = "offen" | "erledigt";
export type QsPrio = "normal" | "hoch";

export interface QsDocument {
  id: string;
  task_id: string | null;
  title: string;
  file_url: string | null;
  file_name: string | null;
  created_at: string;
}

export interface QsTask {
  id: string;
  title: string;
  description: string | null;
  status: QsStatus;
  priority: QsPrio;
  due_on: string | null;
  assigned_to: string | null;
  created_by: string | null;
  done_at: string | null;
  done_by: string | null;
  created_at: string;
  assignee?: { full_name: string | null } | null;
  ersteller?: { full_name: string | null } | null;
  erlediger?: { full_name: string | null } | null;
  documents?: QsDocument[];
}

export function useQsTasks() {
  return useQuery({
    queryKey: ["qs-tasks"],
    // Es gibt zwei getrennte Blicke auf dieselbe Tabelle: Die Leitung sieht alle
    // Aufgaben, ein Mitarbeiter nur seine. Das erzwingt die RLS serverseitig
    // (Migration 035) — der Client fragt schlicht "gib mir, was ich sehen darf".
    queryFn: async () => {
      const { data, error } = await supabase
        .from("qs_tasks")
        .select(
          `*,
           assignee:profiles!qs_tasks_assigned_to_fkey(full_name),
           ersteller:profiles!qs_tasks_created_by_fkey(full_name),
           erlediger:profiles!qs_tasks_done_by_fkey(full_name),
           documents:qs_documents(*)`
        )
        .order("status")
        .order("priority", { ascending: false })
        .order("due_on", { nullsFirst: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as QsTask[];
    },
  });
}

/** Zahl für den Punkt auf der Startseiten-Kachel. */
export function useOffeneQs() {
  return useQuery({
    queryKey: ["qs-offen"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("qs_tasks")
        .select("id", { count: "exact", head: true })
        .eq("status", "offen");
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 30_000,
  });
}

/** Kollegen für die Zuweisung. */
export function useTeam() {
  return useQuery({
    queryKey: ["team"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, role, allowed_modules, permissions, personalnummer, urlaubstage_jahr, im_lohn")
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as {
        id: string;
        full_name: string | null;
        role: string;
        allowed_modules: string[] | null;
        permissions: string[] | null;
        personalnummer: string | null;
        urlaubstage_jahr: number;
        im_lohn: boolean;
      }[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useSpeichereQs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (t: {
      id?: string;
      title: string;
      description?: string | null;
      priority: QsPrio;
      due_on?: string | null;
      assigned_to?: string | null;
    }) => {
      const { data: session } = await supabase.auth.getSession();
      const { data, error } = await supabase
        .from("qs_tasks")
        .upsert(
          {
            ...(t.id ? { id: t.id } : {}),
            title: t.title.trim(),
            description: t.description?.trim() || null,
            priority: t.priority,
            due_on: t.due_on || null,
            assigned_to: t.assigned_to || null,
            ...(t.id ? {} : { created_by: session.session?.user.id ?? null }),
          },
          { onConflict: "id" }
        )
        .select("id")
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qs-tasks"] });
      qc.invalidateQueries({ queryKey: ["qs-offen"] });
    },
  });
}

/** Abhaken — optimistisch, das Häkchen darf nicht ruckeln. */
export function useHakeQsAb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, erledigt }: { id: string; erledigt: boolean }) => {
      const { data: session } = await supabase.auth.getSession();
      const { error } = await supabase
        .from("qs_tasks")
        .update({
          status: erledigt ? "erledigt" : "offen",
          done_at: erledigt ? new Date().toISOString() : null,
          done_by: erledigt ? (session.session?.user.id ?? null) : null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async ({ id, erledigt }) => {
      await qc.cancelQueries({ queryKey: ["qs-tasks"] });
      const prev = qc.getQueryData<QsTask[]>(["qs-tasks"]);
      qc.setQueryData<QsTask[]>(["qs-tasks"], (t) =>
        (t ?? []).map((x) =>
          x.id === id ? { ...x, status: erledigt ? "erledigt" : "offen" } : x
        )
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["qs-tasks"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["qs-tasks"] });
      qc.invalidateQueries({ queryKey: ["qs-offen"] });
    },
  });
}

export function useLoescheQs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("qs_tasks").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qs-tasks"] });
      qc.invalidateQueries({ queryKey: ["qs-offen"] });
    },
  });
}

/**
 * Dokument anhängen. Datei landet im Storage-Bucket `qs`, der Datensatz zeigt
 * darauf. Ohne Bucket würde die Datei sonst nur als Name existieren.
 */
export function useLadeQsDokumentHoch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, file }: { taskId: string | null; file: File }) => {
      const { data: session } = await supabase.auth.getSession();
      const me = session.session?.user.id ?? "anon";
      const pfad = `${taskId ?? "allgemein"}/${Date.now()}-${file.name.replace(/[^\w.\-]/g, "_")}`;

      const { error: upErr } = await supabase.storage.from("qs").upload(pfad, file, {
        cacheControl: "3600",
        upsert: false,
      });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from("qs").getPublicUrl(pfad);

      const { error } = await supabase.from("qs_documents").insert({
        task_id: taskId,
        title: file.name,
        file_url: pub.publicUrl,
        file_name: file.name,
        mime_type: file.type || null,
        size_bytes: file.size,
        uploaded_by: me,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qs-tasks"] }),
  });
}
